import type { TpslOrderType } from '@flipster/apis/v2/trade/types';

/** BBO(best counterparty) price-match strategy. Limit + this strategy expresses BBO (자체 실행 타입 없음). */
export const BBO_PRICE_MATCH_STRATEGY = 'PRICE_MATCH_STRATEGY_COUNTERPARTY_1';

export type TpslLegType = 'tp' | 'sl';
export type TpslPositionType = 'open' | 'pending';

/** Confirm 시 전송할 요청 종류. */
export type TpslRequestKind =
  | 'delete' // 기존 주문 삭제(빈 trigger로 업데이트)
  | 'replace' // 기존 주문 삭제 후 재생성(값 변경 업데이트)
  | 'upsert'; // 신규 생성(add) 또는 pending 업서트

/**
 * Confirm 시 어떤 요청을 보낼지 결정한다. (api.tsx updateTpsl 의 순수 결정 로직)
 * - open + 기존 주문 선택됨 + trigger price 비움 → 'delete'
 * - open + 기존 주문 선택됨 + trigger price 있음 → 'replace' (delete 후 재생성)
 * - open + 신규(미선택)                          → 'upsert' (add)
 * - pending                                       → 'upsert'
 */
export const resolveTpslRequest = ({
  positionType,
  hasSelectedOrder,
  isEmptyTriggerInput,
}: {
  positionType: TpslPositionType;
  hasSelectedOrder: boolean;
  isEmptyTriggerInput: boolean;
}): TpslRequestKind => {
  if (positionType === 'open' && hasSelectedOrder) {
    return isEmptyTriggerInput ? 'delete' : 'replace';
  }
  return 'upsert';
};

export interface TpslExecutionFieldsBase {
  orderType: TpslOrderType;
  limitPrice: string | null;
  priceMatchStrategy: string | null;
}

/**
 * 요청에 보낼 (orderType, limitPrice, priceMatchStrategy) 트리플을 결정한다.
 * BBO 는 Limit + priceMatchStrategy 로 표현하며, limitPrice 와 priceMatchStrategy 는 상호 배타적이다
 * (사용 안 하는 쪽은 null → amend 시 서버가 슬롯 유지).
 * - canLimitTpsl=false 이거나 Limit Order 체크 해제 → 항상 Market.
 */
export const buildTpslExecutionFieldsBase = ({
  canLimitTpsl,
  isLimitTpslOrder,
  isBbo,
  limitPrice,
}: {
  canLimitTpsl: boolean;
  isLimitTpslOrder: boolean;
  isBbo: boolean;
  limitPrice: string;
}): TpslExecutionFieldsBase => {
  if (!canLimitTpsl || !isLimitTpslOrder) {
    return { orderType: 'Market', limitPrice: null, priceMatchStrategy: null };
  }
  return {
    orderType: 'Limit',
    limitPrice: isBbo ? null : limitPrice,
    priceMatchStrategy: isBbo ? BBO_PRICE_MATCH_STRATEGY : null,
  };
};

export interface PrefixedExecutionFields {
  orderTpOrderType?: TpslOrderType;
  orderTpLimitPrice?: string | null;
  orderTpPriceMatchStrategy?: string | null;
  orderSlOrderType?: TpslOrderType;
  orderSlLimitPrice?: string | null;
  orderSlPriceMatchStrategy?: string | null;
}

/**
 * base 트리플을 leg(tp/sl) 접두 필드로 변환한다. (Multi-position·amend 경로용)
 * Market 은 limit/priceMatchStrategy 키를 생략한다(서버 Market 기본값 사용).
 */
export const toPrefixedExecutionFields = (
  base: TpslExecutionFieldsBase,
  legType: TpslLegType,
): PrefixedExecutionFields => {
  if (base.orderType === 'Market') {
    return legType === 'tp' ? { orderTpOrderType: 'Market' } : { orderSlOrderType: 'Market' };
  }
  if (legType === 'tp') {
    return {
      orderTpOrderType: base.orderType,
      orderTpLimitPrice: base.limitPrice,
      orderTpPriceMatchStrategy: base.priceMatchStrategy,
    };
  }
  return {
    orderSlOrderType: base.orderType,
    orderSlLimitPrice: base.limitPrice,
    orderSlPriceMatchStrategy: base.priceMatchStrategy,
  };
};
